import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../api/api";

/* REUSABLE COMPONENTS */
const Field = ({
  label,
  type,
  textarea,
  value,
  onChange,
  name,
  readOnly,
  children
}) => (
  <div>
    <span className="label">{label}</span>

    {type === "select" ? (
      <select
        value={value || ""}
        onChange={onChange}
        name={name}
        disabled={readOnly}
      >
        {children}
      </select>
    ) : textarea ? (
      <textarea
        value={value || ""}
        onChange={onChange}
        name={name}
        readOnly={readOnly}
      />
    ) : (
      <input
        type={type || "text"}   // 👈 ensures previous behavior
        value={value || ""}
        onChange={onChange}
        name={name}
        readOnly={readOnly}
      />
    )}
  </div>
);

const Select = ({ label, value, onChange, name, readOnly }) => (
  <div>
    <span className="label">{label}</span>
    <select value={value || ""} onChange={onChange} name={name} disabled={readOnly}>
      <option value="">Select</option>
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  </div>
);

const File = ({ label }) => (
  <div>
    <span className="label">{label}</span>
    <input type="file" />
  </div>
);

const TabLink = ({ id, label, activeTab, onClick }) => (
  <div
    className={`tab-link ${activeTab === id ? "active" : ""}`}
    onClick={onClick}
  >
    {label}
  </div>
);

const TabPage = ({ id, title, children, activeTab }) =>
  activeTab === id && (
    <div className="tab-page">
      <div className="section-title">{title}</div>
      <div className="row">{children}</div>
    </div>
  );

function PartnerProfile() {
  const [activeTab, setActiveTab] = useState("basic");
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    id: "",
    business_name: "",
    owner_name: "",
    mobile: "",
    whatsapp_number: "",
    email: "",
    alternate_contact: "",
    website: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    service_location: "",
    service_type: "",
    sub_services: "",
    years_experience: "",
    business_since: "",
    working_hours: "",
    emergency_service: false,
    availability: "",
    aadhaar_number: "",
    pan_number: "",
    gst_number: "",
    udyam_number: "",
    police_verification: false,
    license_certificate: "",
    account_holder: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    upi_id: "",
    payment_modes: "",
    business_logo: "",
    provider_photo: "",
    id_proof_photo: "",
    shop_photo: "",
    portfolio_photos: "",
    support_number: "",
    support_email: "",
    response_time: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const token = localStorage.getItem("token");
      
      if (!user || !token) {
        navigate("/");
        return;
      }
      
      if (user.role !== "partner") {
        navigate("/customer-profile");
        return;
      }
      
      try {
        // Fetch fresh data from backend
        const response = await authAPI.getProfile(token);
        if (response.success) {
          const freshData = response.data;
          // Update localStorage with fresh data
          localStorage.setItem("user", JSON.stringify(freshData));
          setUserData(freshData);
          setFormData(prev => ({
            ...prev,
            ...freshData
          }));
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        // Fallback to localStorage data
        setUserData(user);
        setFormData(prev => ({
          ...prev,
          ...user
        }));
      }
    };
    
    fetchUserData();
  }, [navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async (tabFields) => {
    setLoading(true);
    setMessage("");
    
    try {
      const token = localStorage.getItem("token");
      const dataToUpdate = {};
      
      // Only include fields from current tab
      tabFields.forEach(field => {
        if (formData[field] !== undefined) {
          dataToUpdate[field] = formData[field];
        }
      });

      const response = await authAPI.updatePartner(dataToUpdate, token);
      
      if (response.success) {
        setMessage("✅ Updated successfully!");
        // Update localStorage
        const updatedUser = { ...userData, ...response.data };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUserData(updatedUser);
        setFormData(prev => ({ ...prev, ...response.data }));
        
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (error) {
      setMessage("❌ " + (error.message || "Update failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-wrapper">

      {/* LEFT TABS */}
      <div className="tabs">
        <TabLink id="basic" label="Basic Information" activeTab={activeTab} onClick={() => setActiveTab("basic")} />
        <TabLink id="address" label="Address Details" activeTab={activeTab} onClick={() => setActiveTab("address")} />
        <TabLink id="service" label="Service Details" activeTab={activeTab} onClick={() => setActiveTab("service")} />
        {/* <TabLink id="pricing" label="Pricing Details" activeTab={activeTab} onClick={() => setActiveTab("pricing")} />
        <TabLink id="employee" label="Employee / Staff Details" activeTab={activeTab} onClick={() => setActiveTab("employee")} /> */}
        <TabLink id="identity" label="Identity & Compliance" activeTab={activeTab} onClick={() => setActiveTab("identity")} />
        <TabLink id="bank" label="Bank & Payment Details" activeTab={activeTab} onClick={() => setActiveTab("bank")} />
        {/* <TabLink id="tools" label="Tools & Equipment" activeTab={activeTab} onClick={() => setActiveTab("tools")} /> */}
        <TabLink id="media" label="Media Uploads" activeTab={activeTab} onClick={() => setActiveTab("media")} />
        <TabLink id="support" label="Customer Support" activeTab={activeTab} onClick={() => setActiveTab("support")} />
        {/* <TabLink id="rating" label="Ratings & Quality" activeTab={activeTab} onClick={() => setActiveTab("rating")} />
        <TabLink id="terms" label="Terms & Consent" activeTab={activeTab} onClick={() => setActiveTab("terms")} /> */}
      </div>

      {/* RIGHT CONTENT */}
      <div className="tab-content">
        {message && <div className="update-message">{message}</div>}

        <TabPage id="basic" title="Basic Information" activeTab={activeTab}>
          <Field 
            label="Business Name" 
            value={formData?.business_name || ""} 
            onChange={(e) => handleChange('business_name', e.target.value)}
          />
          <Field 
            label="Owner Name" 
            value={formData?.owner_name || ""} 
            onChange={(e) => handleChange('owner_name', e.target.value)}
          />
          <Field 
            label="Mobile Number" 
            value={formData?.mobile || ""} 
            onChange={(e) => handleChange('mobile', e.target.value)}
          />
          <Field 
            label="WhatsApp Number" 
            value={formData?.whatsapp_number || ""}
            onChange={(e) => handleChange('whatsapp_number', e.target.value)}
          />
          <Field label="Email" value={formData?.email || ""} readOnly />
          <Field 
            label="Alternate Contact" 
            value={formData?.alternate_contact || ""}
            onChange={(e) => handleChange('alternate_contact', e.target.value)}
          />
          <Field 
            label="Website" 
            value={formData?.website || ""}
            onChange={(e) => handleChange('website', e.target.value)}
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['business_name', 'owner_name', 'mobile', 'whatsapp_number', 'alternate_contact', 'website'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        <TabPage id="address" title="Address Details" activeTab={activeTab}>
          <Field 
            label="Complete Address" 
            textarea 
            value={formData?.address || ""}
            onChange={(e) => handleChange('address', e.target.value)}
          />
          <Field 
            label="City" 
            value={formData?.city || ""}
            onChange={(e) => handleChange('city', e.target.value)}
          />
          <Field 
            label="State" 
            value={formData?.state || ""}
            onChange={(e) => handleChange('state', e.target.value)}
          />
          <Field 
            label="Pincode" 
            value={formData?.pincode || ""}
            onChange={(e) => handleChange('pincode', e.target.value)}
          />
          <Field 
            label="Service Location Coverage" 
            value={formData?.service_location || ""}
            onChange={(e) => handleChange('service_location', e.target.value)}
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['address', 'city', 'state', 'pincode', 'service_location'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        <TabPage id="service" title="Service Details" activeTab={activeTab}>
          <Field
            label="Type of Service"
            type="select"
            value={formData?.service_type || ""}
            onChange={(e) => handleChange("service_type", e.target.value)}
          >
            <option value="">Select service</option>
            <option value="salon">Salon</option>
            <option value="plumber">Plumber</option>
            <option value="car_wash">Car Wash</option>
            <option value="carpenter">Carpenter</option>
            <option value="ironing">Ironing</option>
            <option value="marketing">Marketing</option>
            <option value="sales_mrs">Sales & Mrs</option>
            <option value="gas_connection">Gas Connection</option>
            <option value="water_supplier">Water Supplier</option>
            <option value="car_washer">Car Washer</option>
          </Field>
          <Field
            label="Sub Services"
            value={formData?.sub_services || ""}
            onChange={(e) => handleChange('sub_services', e.target.value)}
          />
          <Field 
            label="Years of Experience" 
            value={formData?.years_experience || ""}
            onChange={(e) => handleChange('years_experience', e.target.value)}
          />
          <Field 
            label="Business Working Since" 
            value={formData?.business_since || ""}
            onChange={(e) => handleChange('business_since', e.target.value)}
          />
          <Field 
            label="Working Hours" 
            value={formData?.working_hours || ""}
            onChange={(e) => handleChange('working_hours', e.target.value)}
          />
          <Select 
            label="Emergency Service?" 
            value={formData?.emergency_service || false}
            onChange={(e) => handleChange('emergency_service', e.target.value === 'Yes')}
          />
          <Field 
            label="Availability" 
            value={formData?.availability || ""}
            onChange={(e) => handleChange('availability', e.target.value)}
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['service_type', 'sub_services', 'years_experience', 'business_since', 'working_hours', 'emergency_service', 'availability'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        <TabPage id="identity" title="Identity & Compliance" activeTab={activeTab}>
          <Field 
            label="Aadhaar Number" 
            value={formData?.aadhaar_number || ""}
            onChange={(e) => handleChange('aadhaar_number', e.target.value)}
          />
          <Field 
            label="PAN Number" 
            value={formData?.pan_number || ""}
            onChange={(e) => handleChange('pan_number', e.target.value)}
          />
          <Field 
            label="GST Number" 
            value={formData?.gst_number || ""}
            onChange={(e) => handleChange('gst_number', e.target.value)}
          />
          <Field 
            label="Udyam Number" 
            value={formData?.udyam_number || ""}
            onChange={(e) => handleChange('udyam_number', e.target.value)}
          />
          <Select 
            label="Police Verification" 
            value={formData?.police_verification || false}
            onChange={(e) => handleChange('police_verification', e.target.value === 'Yes')}
          />
          <Field 
            label="License / Certificate" 
            value={formData?.license_certificate || ""}
            onChange={(e) => handleChange('license_certificate', e.target.value)}
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['aadhaar_number', 'pan_number', 'gst_number', 'udyam_number', 'police_verification', 'license_certificate'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        <TabPage id="bank" title="Bank & Payment Details" activeTab={activeTab}>
          <Field 
            label="Account Holder" 
            value={formData?.account_holder || ""}
            onChange={(e) => handleChange('account_holder', e.target.value)}
          />
          <Field 
            label="Bank Name" 
            value={formData?.bank_name || ""}
            onChange={(e) => handleChange('bank_name', e.target.value)}
          />
          <Field 
            label="Account Number" 
            value={formData?.account_number || ""}
            onChange={(e) => handleChange('account_number', e.target.value)}
          />
          <Field 
            label="IFSC Code" 
            value={formData?.ifsc_code || ""}
            onChange={(e) => handleChange('ifsc_code', e.target.value)}
          />
          <Field 
            label="UPI ID" 
            value={formData?.upi_id || ""}
            onChange={(e) => handleChange('upi_id', e.target.value)}
          />
          <Field 
            label="Payment Modes Accepted" 
            value={formData?.payment_modes || ""}
            onChange={(e) => handleChange('payment_modes', e.target.value)}
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['account_holder', 'bank_name', 'account_number', 'ifsc_code', 'upi_id', 'payment_modes'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        <TabPage id="media" title="Media Uploads" activeTab={activeTab}>
          <File label="Business Logo" />
          <File label="Service Provider Photo" />
          <File label="ID Proof Photo" />
          <File label="Shop Photo" />
          <File label="Work Portfolio Photos" />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['business_logo', 'provider_photo', 'id_proof_photo', 'shop_photo', 'portfolio_photos'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        <TabPage id="support" title="Customer Support" activeTab={activeTab}>
          <Field 
            label="Customer Care Number" 
            value={formData?.support_number || ""}
            onChange={(e) => handleChange('support_number', e.target.value)}
          />
          <Field 
            label="Support Email" 
            value={formData?.support_email || ""}
            onChange={(e) => handleChange('support_email', e.target.value)}
          />
          <Field 
            label="Response Time" 
            value={formData?.response_time || ""}
            onChange={(e) => handleChange('response_time', e.target.value)}
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['support_number', 'support_email', 'response_time'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

      </div>
    </div>
  );
}

export default PartnerProfile;
